import React from "react";
import PropTypes from "prop-types";

const TRIGGER_OPTIONS = [
  { value: "soft", label: "Soft threshold" },
  { value: "hard", label: "Hard threshold" },
  { value: "predictive", label: "Predictive (ETA)" },
  { value: "spike", label: "Spike detected" },
];

const ACTION_OPTIONS = [
  { value: "notify", label: "Notify" },
  { value: "approval_gate", label: "Approval gate" },
];

const ROLE_OPTIONS = [
  { value: "finance_owner", label: "Finance Owner" },
  { value: "ops_owner", label: "Ops Owner" },
  { value: "cfo", label: "CFO" },
  { value: "exec", label: "Exec" },
];

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "slack", label: "Slack" },
];

const StepEditorPanel = ({ step, onStepChange }) => {
  if (!step) {
    return (
      <div className="bg-card border border-card-bd rounded-card p-4 min-w-0">
        <div className="border-b border-border pb-3 mb-4">
          <h3 className="text-sm font-semibold text-text">Step details</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-sm font-medium text-text-2 mb-1">No step selected</p>
          <p className="text-sm text-text-2">Select a step on the left to edit.</p>
        </div>
      </div>
    );
  }

  const toggleChannel = (ch) => {
    const current = step.delivery_channels || [];
    const next = current.includes(ch)
      ? current.filter((c) => c !== ch)
      : [...current, ch];
    onStepChange({ ...step, delivery_channels: next });
  };

  const recipientRole =
    step.delivery_recipients && step.delivery_recipients.length > 0
      ? step.delivery_recipients[0].role || "finance_owner"
      : "finance_owner";

  const setRecipientRole = (role) => {
    onStepChange({
      ...step,
      delivery_recipients: [{ type: "role", role }],
    });
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0">
      <div className="border-b border-border pb-3 mb-4">
        <h3 className="text-sm font-semibold text-text">Step details</h3>
      </div>

      <div className="space-y-4">
        {/* Trigger level */}
        <div>
          <label htmlFor="step-trigger" className="block text-xs font-medium text-text-2 mb-1">
            Trigger
          </label>
          <select
            id="step-trigger"
            className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
            value={step.trigger_level}
            onChange={(e) => onStepChange({ ...step, trigger_level: e.target.value })}
          >
            {TRIGGER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Action */}
        <div>
          <label htmlFor="step-action" className="block text-xs font-medium text-text-2 mb-1">
            Action
          </label>
          <select
            id="step-action"
            className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
            value={step.action}
            onChange={(e) =>
              onStepChange({
                ...step,
                action: e.target.value,
                approval_role: e.target.value === "approval_gate" ? (step.approval_role || "cfo") : null,
              })
            }
          >
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Delivery channels */}
        <div>
          <span className="block text-xs font-medium text-text-2 mb-2">Delivery channels</span>
          <div className="flex gap-4">
            {CHANNELS.map((ch) => (
              <label key={ch.value} className="flex items-center gap-2 text-sm text-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={(step.delivery_channels || []).includes(ch.value)}
                  onChange={() => toggleChannel(ch.value)}
                />
                {ch.label}
              </label>
            ))}
          </div>
        </div>

        {/* Recipient role */}
        <div>
          <label htmlFor="step-recipient-role" className="block text-xs font-medium text-text-2 mb-1">
            Recipient role
          </label>
          <select
            id="step-recipient-role"
            className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
            value={recipientRole}
            onChange={(e) => setRecipientRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Delivery enabled */}
        <div>
          <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
            <input
              type="checkbox"
              checked={!!step.delivery_enabled}
              onChange={(e) => onStepChange({ ...step, delivery_enabled: e.target.checked })}
            />
            Delivery enabled
          </label>
        </div>

        {/* Approval role — only when action is approval_gate */}
        {step.action === "approval_gate" && (
          <div>
            <label htmlFor="step-approval-role" className="block text-xs font-medium text-text-2 mb-1">
              Approval role
            </label>
            <select
              id="step-approval-role"
              className="w-full p-2 border border-border rounded-ctrl text-sm bg-card text-text"
              value={step.approval_role || "cfo"}
              onChange={(e) => onStepChange({ ...step, approval_role: e.target.value })}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

StepEditorPanel.propTypes = {
  step: PropTypes.object,
  onStepChange: PropTypes.func.isRequired,
};

export default StepEditorPanel;
